import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse, IdentityBoard, MigrationNarrative, JourneyBlocksData } from '@/api/types';
import { Loader2, Plane, Home, Building2, GraduationCap, Globe } from 'lucide-react';
import clsx from 'clsx';

interface JourneyBlockProps {
  onComplete: () => void;
}

const MIGRATION_NARRATIVES: {
  value: MigrationNarrative;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    value: 'fresh_off_boat',
    label: 'Fresh Off the Boat',
    description: '< 2 years, still adjusting to life abroad',
    icon: Plane,
  },
  {
    value: 'finding_feet',
    label: 'Finding My Feet',
    description: '2-5 years, building my life here',
    icon: Building2,
  },
  {
    value: 'settled_connected',
    label: 'Settled & Connected',
    description: '5-10 years, well established',
    icon: Home,
  },
  {
    value: 'fully_rooted',
    label: 'Fully Rooted',
    description: '10+ years or citizen',
    icon: Home,
  },
  {
    value: 'abcd_second_gen',
    label: 'Second Generation',
    description: 'Born abroad or raised mostly abroad',
    icon: GraduationCap,
  },
  {
    value: 'returned_nri',
    label: 'Returned NRI',
    description: 'Went back to India, came again',
    icon: Plane,
  },
  {
    value: 'global_nomad',
    label: 'Global Nomad',
    description: 'Lived in multiple countries',
    icon: Globe,
  },
];

export default function JourneyBlock({ onComplete }: JourneyBlockProps) {
  const queryClient = useQueryClient();

  // Fetch existing board data
  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['identity-board'],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>('/identity-board'),
  });

  const board = boardData?.data;
  const journeyBlocks = board?.journey;

  // Form state
  const [migrationNarrative, setMigrationNarrative] = useState<MigrationNarrative | ''>('');

  // Initialize from board data
  useEffect(() => {
    if (journeyBlocks) {
      setMigrationNarrative(journeyBlocks.migrationNarrative || '');
    }
  }, [journeyBlocks]);

  // Update mutation - use block-specific endpoint
  const updateJourneyBlocks = useMutation({
    mutationFn: async (data: JourneyBlocksData) => {
      return api.patch<ApiResponse<IdentityBoard>>('/identity-board/blocks/journey', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['identity-board'] });
      queryClient.invalidateQueries({ queryKey: ['identity-board-completeness'] });
      onComplete();
    },
  });

  const handleSubmit = () => {
    if (!migrationNarrative) return;

    updateJourneyBlocks.mutate({
      migrationNarrative,
    });
  };

  if (boardLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Migration Narrative */}
      <div>
        <label className="label">What's your migration story? *</label>
        <p className="text-sm text-gray-500 mb-4">
          Select the journey that best describes your path
        </p>
        <div className="grid gap-3">
          {MIGRATION_NARRATIVES.map((narrative) => {
            const Icon = narrative.icon;
            const isSelected = migrationNarrative === narrative.value;

            return (
              <button
                key={narrative.value}
                type="button"
                onClick={() => setMigrationNarrative(narrative.value)}
                className={clsx(
                  'p-4 rounded-xl border-2 text-left transition-all flex items-start gap-4',
                  isSelected
                    ? 'border-accent-500 bg-accent-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div
                  className={clsx(
                    'w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0',
                    isSelected ? 'bg-accent-500 text-white' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <span className="font-semibold block">{narrative.label}</span>
                  <span className="text-sm text-gray-600">{narrative.description}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!migrationNarrative || updateJourneyBlocks.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {updateJourneyBlocks.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Save & Continue'
          )}
        </button>
        {updateJourneyBlocks.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
